import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {
    SLASH_MENU_OFFSET,
    SLASH_MENU_WIDTH,
    SLASH_QUERY_AUTOCLOSE_LENGTH,
    SLASH_QUERY_AUTOCLOSE_WORDS,
    SLASH_QUERY_STOP_RE,
    brickSupportsConfig,
    comparePaths,
    findNextSelectableOptionIndex,
    getDefaultBrickInsertOptions,
    getFilteredBrickOptions,
    getTextFromRange,
    isSelectableInsertOption,
    resolveBrickInsertBehavior
} from "./brick-utils";

export default function useRichTextBrickController({
    bricks,
    insertOptions,
    enableBricks,
    defaultBrick,
    bridgeRef,
    editorHostRef
}) {
    const [toolbarHost, setToolbarHost] = useState(null);
    const toolbarHostRef = useRef(null);
    const [showBrickEditor, setShowBrickEditor] = useState(false);
    const showBrickEditorRef = useRef(showBrickEditor);
    const [brickPanelStyle, setBrickPanelStyle] = useState(null);
    const [brickInsertMode, setBrickInsertMode] = useState('insert');
    const brickInsertModeRef = useRef(brickInsertMode);
    const [brickInsertSource, setBrickInsertSource] = useState('toolbar');
    const brickInsertSourceRef = useRef(brickInsertSource);
    const [brickSearch, setBrickSearch] = useState('');
    const [slashMenuPosition, setSlashMenuPosition] = useState(null);
    const slashMenuPositionRef = useRef(null);
    const [highlightedQuickOptionIndex, setHighlightedQuickOptionIndex] = useState(0);
    const highlightedQuickOptionIndexRef = useRef(0);
    const highlightedQuickOptionIdRef = useRef(null);
    const [activeBrickId, setActiveBrickId] = useState(defaultBrick?.id || null);
    const [editingToken, setEditingToken] = useState(null);
    const [brickConfigSnapshot, setBrickConfigSnapshot] = useState(null);
    const [brickConfig, setBrickConfig] = useState(defaultBrick?.defaultConfig || {});
    const editingBrickElementRef = useRef(null);
    const brickAnchorElementRef = useRef(null);
    const brickPanelRef = useRef(null);
    const brickSearchRef = useRef();
    const pendingSlashCommandRef = useRef(null);
    const activeSlashCommandRef = useRef(null);

    const getBrickById = useCallback((id) => {
        if (!id) return null;
        return bricks.find(brick => brick.id === id) || null;
    }, [bricks]);

    const matchBrickByToken = useCallback((token) => {
        for (const brick of bricks) {
            const result = brick.matchToken?.(token);
            if (result) {
                return {
                    ...result,
                    configurable: brickSupportsConfig(brick)
                };
            }
        }
        return null;
    }, [bricks]);

    const quickInsertOptions = useMemo(() => (
        insertOptions?.length
            ? insertOptions
            : getDefaultBrickInsertOptions(bricks)
    ), [insertOptions, bricks]);

    const filteredQuickOptions = useMemo(
        () => getFilteredBrickOptions(brickSearch, quickInsertOptions),
        [brickSearch, quickInsertOptions]
    );

    const setToolbarHostNode = useCallback((node) => {
        toolbarHostRef.current = node;
        setToolbarHost(node);
    }, []);

    const setHighlightedInsertOption = useCallback((nextIndex, options = filteredQuickOptions) => {
        highlightedQuickOptionIndexRef.current = nextIndex;
        highlightedQuickOptionIdRef.current = isSelectableInsertOption(options[nextIndex])
            ? options[nextIndex].id
            : null;
        setHighlightedQuickOptionIndex(nextIndex);
    }, [filteredQuickOptions]);

    const getHighlightedInsertOption = useCallback((options = filteredQuickOptions) => {
        const candidate = options[Math.min(highlightedQuickOptionIndexRef.current, Math.max(options.length - 1, 0))];
        if (isSelectableInsertOption(candidate)) {
            return candidate;
        }

        const nextIndex = findNextSelectableOptionIndex(options, highlightedQuickOptionIndexRef.current, 1);
        return nextIndex === -1 ? null : options[nextIndex];
    }, [filteredQuickOptions]);

    const clearSlashCommand = useCallback(() => {
        activeSlashCommandRef.current = null;
        pendingSlashCommandRef.current = null;
        slashMenuPositionRef.current = null;
        setSlashMenuPosition(null);
    }, []);

    const closeBrickInsert = useCallback(() => {
        setShowBrickEditor(false);
        setBrickInsertSource('toolbar');
        setBrickSearch('');
        setHighlightedInsertOption(0, []);
        setEditingToken(null);
        editingBrickElementRef.current = null;
        brickAnchorElementRef.current = null;
        clearSlashCommand();
    }, [clearSlashCommand, setHighlightedInsertOption]);

    const resolveBrickIdForToken = useCallback((token, preferredBrickId = null) => {
        const preferred = getBrickById(preferredBrickId);
        if (preferred) return preferred.id;
        const matched = matchBrickByToken(token);
        return matched?.brickId || null;
    }, [getBrickById, matchBrickByToken]);

    const applyBrickConfigToSelection = useCallback((config) => {
        const brick = getBrickById(activeBrickId);
        if (!brick) return;
        const selected = editingBrickElementRef.current || bridgeRef.current?.getSelectedBrickElement?.();
        if (!selected) return;
        const token = brick.buildToken?.(config);
        if (!token) return;
        const label = brick.getLabel ? brick.getLabel(config) : brick.title;
        bridgeRef.current?.updateSelectedBrick?.({
            brick,
            token,
            label,
            selectedElement: selected
        });
    }, [activeBrickId, bridgeRef, getBrickById]);

    const revertBrickConfigSnapshot = useCallback(() => {
        if (!brickConfigSnapshot) return;
        const brick = getBrickById(activeBrickId);
        if (!brick) return;
        const reverted = {...(brick.defaultConfig || {}), ...brickConfigSnapshot};
        setBrickConfig(reverted);
        applyBrickConfigToSelection(reverted);
    }, [activeBrickId, applyBrickConfigToSelection, brickConfigSnapshot, getBrickById]);

    const closeConfigurator = useCallback(({revert = false} = {}) => {
        if (revert) {
            revertBrickConfigSnapshot();
        }

        setShowBrickEditor(false);
        setBrickInsertSource('toolbar');
        clearSlashCommand();
        setEditingToken(null);
        editingBrickElementRef.current = null;
    }, [clearSlashCommand, revertBrickConfigSnapshot]);

    const openBrickInsertConfigurator = useCallback((brickId) => {
        const brick = getBrickById(brickId);
        if (!brick || !brickSupportsConfig(brick)) return;

        setActiveBrickId(brick.id);
        setBrickConfig(brick.defaultConfig || {});
        setBrickConfigSnapshot(null);
        setEditingToken(null);
        editingBrickElementRef.current = null;
        setBrickInsertMode('customize');
        setShowBrickEditor(true);
    }, [getBrickById]);

    const openBrickEditor = useCallback((mode = 'customize', tokenOverride = null, brickIdOverride = null, options = {}) => {
        if (!enableBricks) return;

        const source = options.source || 'toolbar';
        if (
            mode === 'insert'
            && source === 'toolbar'
            && showBrickEditorRef.current
            && brickInsertModeRef.current === mode
            && brickInsertSourceRef.current === source
            && !tokenOverride
        ) {
            closeBrickInsert();
            return;
        }

        setBrickInsertMode(mode);
        if (mode === 'insert') {
            const nextSearch = options.search ?? '';
            const nextFilteredOptions = getFilteredBrickOptions(nextSearch, quickInsertOptions);
            const firstSelectableIndex = findNextSelectableOptionIndex(nextFilteredOptions, -1, 1);

            setBrickInsertSource(source);
            setBrickSearch(nextSearch);
            setHighlightedInsertOption(firstSelectableIndex === -1 ? 0 : firstSelectableIndex, nextFilteredOptions);
            setEditingToken(null);
            editingBrickElementRef.current = null;
            brickAnchorElementRef.current = null;
            setActiveBrickId(defaultBrick?.id || null);
            setBrickConfig(defaultBrick?.defaultConfig || {});
            setBrickConfigSnapshot(null);
            if (source === 'slash') {
                activeSlashCommandRef.current = options.slashCommand || activeSlashCommandRef.current;
                slashMenuPositionRef.current = options.position || null;
                setSlashMenuPosition(options.position || null);
            } else {
                clearSlashCommand();
            }
        } else {
            setBrickInsertSource('toolbar');
            clearSlashCommand();
            editingBrickElementRef.current = bridgeRef.current?.getSelectedBrickElement?.();
            const selectedBrick = bridgeRef.current?.getSelectedBrick?.();
            const selected = tokenOverride
                || selectedBrick?.token
                || window.getSelection?.().toString()
                || '';
            const resolvedBrickId = resolveBrickIdForToken(
                selected,
                brickIdOverride || selectedBrick?.brickId
            );
            const brick = getBrickById(resolvedBrickId);
            if (!brick) return;
            setActiveBrickId(resolvedBrickId);

            const parsed = brick.parseToken?.(selected);
            if (parsed) {
                setBrickConfig({...brick.defaultConfig, ...parsed});
                setEditingToken(tokenOverride || selectedBrick?.token || null);
                setBrickConfigSnapshot(parsed);
            } else {
                setBrickConfig(brick.defaultConfig || {});
                setEditingToken(tokenOverride || selectedBrick?.token || null);
                setBrickConfigSnapshot(null);
            }
        }

        setShowBrickEditor(true);
    }, [
        bridgeRef,
        clearSlashCommand,
        closeBrickInsert,
        defaultBrick,
        enableBricks,
        getBrickById,
        resolveBrickIdForToken,
        setHighlightedInsertOption
    ]);

    const insertBrick = useCallback((brickId, overrideConfig = {}) => {
        const brick = getBrickById(brickId);
        if (!brick) return;

        const config = {...(brick.defaultConfig || {}), ...overrideConfig};
        const token = brick.buildToken?.(config);
        if (!token) return;

        const label = brick.getLabel ? brick.getLabel(config) : brick.title;
        bridgeRef.current?.insertBrick?.({
            brick,
            token,
            label,
            insertSource: brickInsertSourceRef.current,
            slashCommand: activeSlashCommandRef.current
        });

        closeBrickInsert();
        setEditingToken(null);
        editingBrickElementRef.current = null;
    }, [bridgeRef, closeBrickInsert, getBrickById]);

    const removeSelectedBrick = useCallback(() => {
        const selected = editingBrickElementRef.current || bridgeRef.current?.getSelectedBrickElement?.();
        if (!selected) return;
        bridgeRef.current?.removeSelectedBrick?.(selected);
        setEditingToken(null);
        editingBrickElementRef.current = null;
    }, [bridgeRef]);

    const handleInsertOptionSelect = useCallback((option) => {
        const brick = getBrickById(option.id);
        if (resolveBrickInsertBehavior(brick) === 'configure-before-insert') {
            openBrickInsertConfigurator(option.id);
            return;
        }

        insertBrick(option.id);
    }, [getBrickById, insertBrick, openBrickInsertConfigurator]);

    const handleInsertPanelKeyDown = useCallback((event) => {
        const isInsertOpen = showBrickEditorRef.current && brickInsertModeRef.current === 'insert';
        if (!isInsertOpen) return false;

        const quickOptions = filteredQuickOptions;

        if (event.key === 'Escape') {
            event.preventDefault?.();
            event.stopPropagation?.();
            closeBrickInsert();
            return true;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault?.();
            event.stopPropagation?.();
            if (!quickOptions.length) return true;
            const nextIndex = findNextSelectableOptionIndex(quickOptions, highlightedQuickOptionIndexRef.current, 1);
            if (nextIndex !== -1) {
                setHighlightedInsertOption(nextIndex, quickOptions);
            }
            return true;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault?.();
            event.stopPropagation?.();
            if (!quickOptions.length) return true;
            const nextIndex = findNextSelectableOptionIndex(quickOptions, highlightedQuickOptionIndexRef.current, -1);
            if (nextIndex !== -1) {
                setHighlightedInsertOption(nextIndex, quickOptions);
            }
            return true;
        }

        if (event.key === 'Enter') {
            const nextOption = getHighlightedInsertOption(quickOptions);
            if (!nextOption) {
                event.preventDefault?.();
                event.stopPropagation?.();
                return true;
            }

            event.preventDefault?.();
            event.stopPropagation?.();
            handleInsertOptionSelect(nextOption);
            return true;
        }

        return false;
    }, [closeBrickInsert, filteredQuickOptions, getHighlightedInsertOption, handleInsertOptionSelect, setHighlightedInsertOption]);

    const capturePendingSlashCommand = useCallback((command) => {
        pendingSlashCommandRef.current = command;
    }, []);

    const syncSlashMenu = useCallback(() => {
        const editor = bridgeRef.current?.getEditor?.();
        const selection = editor?.model?.document?.selection;
        const activeSlashCommand = activeSlashCommandRef.current || pendingSlashCommandRef.current;

        if (!editor || !selection?.isCollapsed || !activeSlashCommand) {
            if (!selection?.isCollapsed && brickInsertModeRef.current === 'insert' && brickInsertSourceRef.current === 'slash') {
                closeBrickInsert();
            }
            if (!selection?.isCollapsed) {
                clearSlashCommand();
            }
            return;
        }

        const end = selection.getFirstPosition();
        if (!end || end.root !== activeSlashCommand.root || comparePaths(end.path, activeSlashCommand.path) < 0) {
            if (brickInsertModeRef.current === 'insert' && brickInsertSourceRef.current === 'slash') {
                closeBrickInsert();
            }
            clearSlashCommand();
            return;
        }

        let queryText = '';
        try {
            const start = editor.model.createPositionFromPath(activeSlashCommand.root, activeSlashCommand.path);
            queryText = getTextFromRange(editor.model.createRange(start, end));
        } catch (error) {
            closeBrickInsert();
            clearSlashCommand();
            return;
        }

        if (!queryText.startsWith('/')) {
            if (brickInsertModeRef.current === 'insert' && brickInsertSourceRef.current === 'slash') {
                closeBrickInsert();
            }
            clearSlashCommand();
            return;
        }

        if (SLASH_QUERY_STOP_RE.test(queryText.slice(1))) {
            closeBrickInsert();
            return;
        }

        const slashQuery = queryText.slice(1);
        const trimmedSlashQuery = slashQuery.trim();
        const slashQueryWordCount = trimmedSlashQuery ? trimmedSlashQuery.split(/\s+/).length : 0;
        const slashQueryHasMatches = getFilteredBrickOptions(slashQuery, quickInsertOptions).some((option) => option.type !== 'group');
        const shouldAutoCloseForPlainText = !slashQueryHasMatches && (
            slashQuery.includes('/')
            || trimmedSlashQuery.length >= SLASH_QUERY_AUTOCLOSE_LENGTH
            || slashQueryWordCount >= SLASH_QUERY_AUTOCLOSE_WORDS
        );

        if (shouldAutoCloseForPlainText) {
            closeBrickInsert();
            return;
        }

        const selectionDom = window.getSelection?.();
        const domRange = selectionDom?.rangeCount ? selectionDom.getRangeAt(0) : null;
        const domRect = domRange?.getBoundingClientRect?.();
        const viewportPadding = 8;
        const nextPosition = domRect
            ? {
                left: Math.min(
                    Math.max(domRect.left, viewportPadding),
                    Math.max(window.innerWidth - SLASH_MENU_WIDTH - viewportPadding, viewportPadding)
                ),
                top: Math.max(domRect.bottom + SLASH_MENU_OFFSET, viewportPadding),
                anchorTop: domRect.top
            }
            : slashMenuPositionRef.current;

        if (!nextPosition) return;

        activeSlashCommandRef.current = activeSlashCommand;
        pendingSlashCommandRef.current = null;
        slashMenuPositionRef.current = nextPosition;

        openBrickEditor('insert', null, null, {
            source: 'slash',
            search: slashQuery,
            position: nextPosition,
            slashCommand: activeSlashCommand
        });
    }, [bridgeRef, clearSlashCommand, closeBrickInsert, openBrickEditor, quickInsertOptions]);

    const handleChipDoubleClick = useCallback(({token, brickId, anchorElement}) => {
        const brick = getBrickById(brickId);
        if (!brick || !brickSupportsConfig(brick)) return;

        brickAnchorElementRef.current = anchorElement;
        openBrickEditor('customize', token, brickId);
    }, [getBrickById, openBrickEditor]);

    const handleSelectedBrickEnter = useCallback(({token, brickId, anchorElement}) => {
        const resolvedBrickId = brickId || resolveBrickIdForToken(token);
        const brick = getBrickById(resolvedBrickId);
        if (!brick || !brickSupportsConfig(brick)) return false;

        if (anchorElement) {
            brickAnchorElementRef.current = anchorElement;
        }

        openBrickEditor('customize', token, resolvedBrickId);
        return true;
    }, [getBrickById, openBrickEditor, resolveBrickIdForToken]);

    const handleEditHandleClick = useCallback((anchorElement) => {
        if (anchorElement) {
            brickAnchorElementRef.current = anchorElement;
        }
    }, []);

    const handleVelocityBrickClick = useCallback((payload) => {
        if (!payload?.token || !payload?.edit) return;
        const brick = getBrickById(payload.brickId || resolveBrickIdForToken(payload.token));
        if (!brick || !brickSupportsConfig(brick)) return;
        openBrickEditor('customize', payload.token, payload.brickId);
    }, [getBrickById, openBrickEditor, resolveBrickIdForToken]);

    const submitConfigurator = useCallback(() => {
        if (editingToken) {
            setShowBrickEditor(false);
            setEditingToken(null);
            editingBrickElementRef.current = null;
            return;
        }

        insertBrick(activeBrickId || defaultBrick?.id, brickConfig);
    }, [activeBrickId, brickConfig, defaultBrick, editingToken, insertBrick]);

    useEffect(() => {
        if (defaultBrick?.id && !activeBrickId) {
            setActiveBrickId(defaultBrick.id);
        }
    }, [activeBrickId, defaultBrick]);

    useEffect(() => {
        if (!enableBricks) return;
        if (toolbarHostRef.current) {
            setToolbarHost(toolbarHostRef.current);
        }
    }, [enableBricks]);

    useEffect(() => {
        showBrickEditorRef.current = showBrickEditor;
    }, [showBrickEditor]);

    useEffect(() => {
        brickInsertModeRef.current = brickInsertMode;
    }, [brickInsertMode]);

    useEffect(() => {
        brickInsertSourceRef.current = brickInsertSource;
    }, [brickInsertSource]);

    useEffect(() => {
        slashMenuPositionRef.current = slashMenuPosition;
    }, [slashMenuPosition]);

    useEffect(() => {
        const currentHighlightedId = highlightedQuickOptionIdRef.current;
        const persistedIndex = currentHighlightedId
            ? filteredQuickOptions.findIndex((option) => isSelectableInsertOption(option) && option.id === currentHighlightedId)
            : -1;

        if (persistedIndex !== -1) {
            setHighlightedInsertOption(persistedIndex, filteredQuickOptions);
            return;
        }

        const firstSelectableIndex = findNextSelectableOptionIndex(filteredQuickOptions, -1, 1);
        setHighlightedInsertOption(firstSelectableIndex === -1 ? 0 : firstSelectableIndex, filteredQuickOptions);
    }, [filteredQuickOptions, setHighlightedInsertOption]);

    useEffect(() => {
        highlightedQuickOptionIndexRef.current = highlightedQuickOptionIndex;
    }, [highlightedQuickOptionIndex]);

    useEffect(() => {
        highlightedQuickOptionIdRef.current = isSelectableInsertOption(filteredQuickOptions[highlightedQuickOptionIndex])
            ? filteredQuickOptions[highlightedQuickOptionIndex].id
            : null;
    }, [filteredQuickOptions, highlightedQuickOptionIndex]);

    useEffect(() => {
        if (!showBrickEditor || brickInsertMode !== 'insert' || brickInsertSource !== 'toolbar') return;
        const id = requestAnimationFrame(() => brickSearchRef.current?.focus());
        return () => cancelAnimationFrame(id);
    }, [showBrickEditor, brickInsertMode, brickInsertSource]);

    useEffect(() => {
        if (!showBrickEditor || brickInsertMode === 'insert' || !editingToken) return;
        applyBrickConfigToSelection(brickConfig);
    }, [applyBrickConfigToSelection, brickConfig, brickInsertMode, editingToken, showBrickEditor]);

    useEffect(() => {
        if (!showBrickEditor) return;

        const handleKeyDown = (event) => {
            if (brickInsertMode === 'insert') {
                const insertKeyHandled = handleInsertPanelKeyDown(event);
                if (insertKeyHandled) {
                    return;
                }
                return;
            }

            if (event.key !== 'Escape') return;

            event.preventDefault?.();
            event.stopPropagation?.();
            event.stopImmediatePropagation?.();
            closeConfigurator({revert: true});
        };

        const handlePointerDown = (event) => {
            const panel = brickPanelRef.current;
            const toolbar = toolbarHostRef.current;
            if (panel && panel.contains(event.target)) return;
            if (toolbar && toolbar.contains(event.target)) return;

            if (brickInsertMode === 'insert') {
                closeBrickInsert();
                return;
            }

            closeConfigurator({revert: true});
        };

        document.addEventListener('keydown', handleKeyDown, true);
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown, {passive: true});
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [brickInsertMode, closeBrickInsert, closeConfigurator, handleInsertPanelKeyDown, showBrickEditor]);

    const isInsertMode = brickInsertMode === 'insert';
    const activeBrick = getBrickById(activeBrickId);
    const activeBrickToken = activeBrick?.buildToken?.(brickConfig);
    const canApplyBrickConfig = !activeBrick || typeof activeBrick.buildToken !== 'function' || !!activeBrickToken;
    const highlightedOptionId = isSelectableInsertOption(filteredQuickOptions[highlightedQuickOptionIndex])
        ? filteredQuickOptions[highlightedQuickOptionIndex].id
        : null;

    useLayoutEffect(() => {
        if (!showBrickEditor) {
            setBrickPanelStyle(null);
            brickAnchorElementRef.current = null;
            return;
        }

        const updateBrickPanelStyle = (event) => {
            const panel = brickPanelRef.current;
            if (!panel || (event?.type === "scroll" && panel.contains(event.target))) return;
            const viewportPadding = 8;
            const panelRect = panel.getBoundingClientRect();
            const width = Math.min(352, window.innerWidth - (viewportPadding * 2));

            if (brickInsertSource === 'slash' && slashMenuPositionRef.current) {
                let left = slashMenuPositionRef.current.left;
                if ((left + width + viewportPadding) > window.innerWidth) {
                    left = window.innerWidth - width - viewportPadding;
                }
                left = Math.max(viewportPadding, left);

                let top = slashMenuPositionRef.current.top;
                if ((top + panelRect.height + viewportPadding) > window.innerHeight) {
                    top = Math.max(viewportPadding, slashMenuPositionRef.current.anchorTop - panelRect.height - SLASH_MENU_OFFSET);
                }

                setBrickPanelStyle({
                    position: 'fixed',
                    left: `${left}px`,
                    top: `${top}px`,
                    maxHeight: `${window.innerHeight - top - viewportPadding}px`,
                    width: `${width}px`,
                    maxWidth: `calc(100vw - ${viewportPadding * 2}px)`,
                    zIndex: 1200,
                    visibility: 'visible'
                });
                return;
            }

            const connectedBrickAnchor = !isInsertMode
                ? (
                    brickAnchorElementRef.current?.isConnected
                        ? brickAnchorElementRef.current
                        : bridgeRef.current?.resolveSelectedBrickChipAnchor?.() || bridgeRef.current?.resolveBrickChipAnchor?.(editingToken)
                )
                : null;

            if (connectedBrickAnchor && brickAnchorElementRef.current !== connectedBrickAnchor) {
                brickAnchorElementRef.current = connectedBrickAnchor;
            }

            const anchor = isInsertMode
                ? (
                    connectedBrickAnchor
                    || toolbarHostRef.current?.querySelector?.('[data-velocity-brick-button="true"]')
                    || toolbarHostRef.current
                    || editorHostRef.current
                )
                : connectedBrickAnchor;

            if (!anchor || !panel) return;

            const anchorRect = anchor.getBoundingClientRect();

            let left = anchorRect.left;
            if ((left + width + viewportPadding) > window.innerWidth) {
                left = window.innerWidth - width - viewportPadding;
            }
            left = Math.max(viewportPadding, left);

            let top = anchorRect.bottom + 8;
            if ((top + panelRect.height + viewportPadding) > window.innerHeight) {
                top = Math.max(viewportPadding, anchorRect.top - panelRect.height - 8);
            }

            setBrickPanelStyle({
                position: 'fixed',
                left: `${left}px`,
                top: `${top}px`,
                maxHeight: `${window.innerHeight - top - viewportPadding}px`,
                width: `${width}px`,
                maxWidth: `calc(100vw - ${viewportPadding * 2}px)`,
                zIndex: 1200,
                visibility: 'visible'
            });
        };

        // Keep the opening position as fields change; the body scrolls within available space.
        updateBrickPanelStyle();
        window.addEventListener('resize', updateBrickPanelStyle);
        window.addEventListener('scroll', updateBrickPanelStyle, true);

        return () => {
            window.removeEventListener('resize', updateBrickPanelStyle);
            window.removeEventListener('scroll', updateBrickPanelStyle, true);
        };
    }, [activeBrickId, brickInsertMode, brickInsertSource, brickSearch, bridgeRef, editingToken, editorHostRef, isInsertMode, showBrickEditor]);

    return {
        activeBrick,
        activeBrickId,
        brickConfig,
        brickInsertMode,
        brickInsertSource,
        brickPanelRef,
        brickPanelStyle,
        brickSearch,
        brickSearchRef,
        canApplyBrickConfig,
        closeBrickInsert,
        closeConfigurator,
        getBrickById,
        handleChipDoubleClick,
        handleEditHandleClick,
        handleInsertOptionSelect,
        handleInsertPanelKeyDown,
        handleSelectedBrickEnter,
        handleVelocityBrickClick,
        highlightedOptionId,
        insertModeOpen: showBrickEditor && isInsertMode,
        editingToken,
        matchBrickByToken,
        openBrickEditor,
        openBrickInsertConfigurator,
        removeSelectedBrick,
        resolveBrickIdForToken,
        searchRef: brickSearchRef,
        setBrickConfig,
        setBrickSearch,
        setToolbarHostNode,
        submitConfigurator,
        syncSlashMenu,
        capturePendingSlashCommand,
        toolbarHost,
        toolbarHostRef,
        filteredQuickOptions,
        onHoverInsertOption: (index) => setHighlightedInsertOption(index, filteredQuickOptions),
        onCancelInsertPopover: closeBrickInsert,
        onCancelConfigurator: () => closeConfigurator({revert: true}),
        setShowBrickEditor,
        showBrickEditor,
        submitLabel: editingToken ? 'Update' : 'Insert',
        configureModeOpen: showBrickEditor && !isInsertMode,
    };
}
